// Checkout propio en PRODUCCIÓN: la misma canalización ya validada en prueba,
// ejecutada con Bókun Producción + Stripe Live. Acceso controlado en el
// servidor por BOKUN_LIVE_CHECKOUT_MODE (off / admin / public).
import { createServerFn } from "@tanstack/react-start";
import { getRequestHost } from "@tanstack/react-start/server";
import { z } from "zod";
import { StartSchema } from "@/lib/bokun-test-booking.functions";
import type { BookingScreenData } from "@/lib/bokun-test/public-booking.server";

const DENIED = "El pago en línea no está disponible por ahora.";

function origin(): string {
  try {
    const host = getRequestHost();
    if (!host) return "http://localhost:8080";
    return `${host.includes("localhost") ? "http" : "https"}://${host}`;
  } catch {
    return "http://localhost:8080";
  }
}

async function guard(activityId: string): Promise<string | null> {
  const { TEST_ONLY_PRODUCT_IDS } = await import("@/lib/bokun-test/env-context.server");
  if (TEST_ONLY_PRODUCT_IDS.has(activityId)) return DENIED;
  const { liveCheckoutAllowed } = await import("@/lib/bokun-live-access.server");
  return (await liveCheckoutAllowed()) ? null : DENIED;
}

/** ¿Puede este visitante usar el checkout propio de producción? */
export const getLiveCheckoutAccess = createServerFn({ method: "GET" }).handler(async () => {
  const { liveCheckoutAllowed } = await import("@/lib/bokun-live-access.server");
  return { allowed: await liveCheckoutAllowed() };
});

export const getBokunLiveBookingScreen = createServerFn({ method: "GET" })
  .inputValidator((input: { activityId: string; days?: number }) =>
    z
      .object({
        activityId: z.string().trim().regex(/^\d{1,12}$/),
        days: z.number().int().min(1).max(120).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }): Promise<BookingScreenData | { error: string }> => {
    const denied = await guard(data.activityId);
    if (denied) return { error: denied };
    try {
      const { runInBokunEnv } = await import("@/lib/bokun-test/env-context.server");
      const { loadBookingScreen } = await import("@/lib/bokun-test/public-booking.server");
      return await runInBokunEnv("live", () =>
        loadBookingScreen({ activityId: data.activityId, days: data.days ?? 60 }),
      );
    } catch (e) {
      console.error("[bokun-live/screen]", (e as Error).message);
      return { error: "No pudimos cargar la disponibilidad. Vuelve a intentarlo en unos minutos." };
    }
  });

export const startBokunLiveBooking = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => StartSchema.parse(input))
  .handler(async ({ data }) => {
    const denied = await guard(data.activityId);
    if (denied) return { ok: false as const, code: "LIVE_DENIED", error: denied };
    const { runInBokunEnv } = await import("@/lib/bokun-test/env-context.server");
    const { startPublicBooking } = await import("@/lib/bokun-test/public-booking.server");
    return await runInBokunEnv("live", () =>
      startPublicBooking({
        activityId: data.activityId,
        date: data.date,
        startTimeId: data.startTimeId,
        rateId: data.rateId,
        items: data.items,
        contact: data.contact,
        answers: data.answers,
        passengerAnswers: data.passengerAnswers,
        extras: data.extras,
        pickupPlaceId: data.pickupPlaceId,
        dropoffPlaceId: data.dropoffPlaceId,
        pickupNote: data.pickupNote,
        dropoffNote: data.dropoffNote,
        idempotencyKey: data.idempotencyKey,
        expectedAmount: data.expectedAmount,
        cancellationPolicyVersion: data.cancellationPolicyVersion,
        recoveryConsent: data.recoveryConsent,
        resumePath: data.resumePath,
        origin: origin(),
      }),
    );
  });
